import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDelivery } from 'api/hooks/useDelivery';
import Button from 'components/Button';
import { formatDate } from 'components/DatePicker/DateInput';
import { ReceiveDeliveryDialog } from 'components/ReceiveDeliveryDialog/ReceiveDeliveryDialog';
import { useLanguage } from 'context/AppContext';
import { useHasSearchedForDestinationDeliveries } from '../hooks/useHasSearchedForDestinationDeliveries';
import { useTurnaroundDestinationDeliveryId } from '../hooks/useTurnaroundDestinationDeliveryId';
import { useTurnaroundDestinationEntities } from '../hooks/useTurnaroundDestinationEntities';
import { useTurnaroundDestinationForm } from '../hooks/useTurnaroundDestinationForm';
import { useTurnaroundOriginDeliveryId } from '../hooks/useTurnaroundOriginDeliveryId';
import { useTurnaroundSupplier } from '../hooks/useTurnaroundSupplier';
import { TurnaroundEntityImplant, TurnaroundEntitySet } from '../models';
import { TurnaroundDestinationSuccessDialog } from './TurnaroundDestinationSuccessDialog';

type Props = ComponentProps<typeof Button>;

export const TurnaroundNewSurgeryButton = ({ ...props }: Props) => {
  const lang = useLanguage()[0].TurnaroundNewSurgeryButton;

  const [turnaroundDestinationEntities] = useTurnaroundDestinationEntities();
  const [showReceiveDeliveryDialog, setShowReceiveDeliveryDialog] = useState(false);
  const [supplier] = useTurnaroundSupplier();
  const [showTurnaroundSuccessDialog, setShowTurnaroundSuccessDialog] = useState(false);
  const [turnaroundDestinationDeliveryId, setTurnaroundDestinationDeliveryId] = useTurnaroundDestinationDeliveryId();
  const [hasSearchedForDestinationDeliveries] = useHasSearchedForDestinationDeliveries();
  const [turnaroundDestinationForm] = useTurnaroundDestinationForm();

  // Analytics
  const [turnaroundOriginDeliveryId] = useTurnaroundOriginDeliveryId();
  const { data: originDelivery } = useDelivery(turnaroundOriginDeliveryId);
  const { data: currentUser } = useCurrentUser();

  const turnaroundDestinationSetKitInstanceIds = useMemo(
    () =>
      turnaroundDestinationEntities
        .filter(entity => entity.type === 'set')
        .map(entity => (entity as TurnaroundEntitySet).setKitInstanceId),
    [turnaroundDestinationEntities],
  );

  const turnaroundDestinationImplantInstanceIds = useMemo(
    () =>
      turnaroundDestinationEntities
        .filter(entity => entity.type === 'implant')
        .map(entity => (entity as TurnaroundEntityImplant).implantInstanceId),
    [turnaroundDestinationEntities],
  );

  const buttonDisabled = Boolean(
    !turnaroundDestinationEntities.length || !hasSearchedForDestinationDeliveries || turnaroundDestinationDeliveryId,
  );

  const onClick = useCallback(async () => {
    // Show the ReceiveDeliveryDialog
    setShowReceiveDeliveryDialog(true);
  }, []);

  const onCompleteReceiveDelivery = useCallback(
    async (destinationDeliveryId?: number) => {
      if (!destinationDeliveryId) {
        throw new Error('Destination delivery ID is not defined');
      }

      if (!turnaroundOriginDeliveryId) {
        throw new Error('Turnaround origin delivery ID is not defined');
      }

      if (!originDelivery) {
        throw new Error('Origin delivery is not defined');
      }

      if (!currentUser) {
        throw new Error('Current user is not defined');
      }

      await trackEvent(EventType.TurnaroundNewSurgery, {
        current_user: currentUser.userId,
        tenant_name: currentUser.tenantName,
        supplier: originDelivery.supplier.name || '',
        order_number: originDelivery.orderNumber || '',
        surgery_reference: originDelivery.surgery.surgeryReference || '',
        delivery_number: turnaroundOriginDeliveryId.toString(),
        surgeon: originDelivery.surgery?.surgeonName || '',
        surgery_date: originDelivery.surgery?.surgeryDate ? formatDate(originDelivery?.surgery?.surgeryDate) : '',
        set_ids: turnaroundDestinationSetKitInstanceIds,
        has_implants: Boolean(turnaroundDestinationImplantInstanceIds.length),
      });

      setShowReceiveDeliveryDialog(false);

      // Set the destination delivery ID
      setTurnaroundDestinationDeliveryId(destinationDeliveryId);

      setShowTurnaroundSuccessDialog(true);
    },
    [
      turnaroundOriginDeliveryId,
      originDelivery,
      currentUser,
      turnaroundDestinationSetKitInstanceIds,
      turnaroundDestinationImplantInstanceIds,
      setTurnaroundDestinationDeliveryId,
    ],
  );

  const onCloseReceiveDeliveryDialog = useCallback(() => {
    setShowReceiveDeliveryDialog(false);
  }, []);

  return (
    <>
      <Button colors="active on white" disabled={buttonDisabled} onClick={onClick} {...props}>
        {lang.newSurgery}
      </Button>

      {showReceiveDeliveryDialog && (
        <ReceiveDeliveryDialog
          supplierId={supplier?.id}
          surgeonId={turnaroundDestinationForm.surgeon?.id}
          surgeryDate={turnaroundDestinationForm.surgeryDate}
          turnaroundSets={turnaroundDestinationSetKitInstanceIds}
          turnaroundImplants={turnaroundDestinationImplantInstanceIds}
          turnaroundOriginDeliveryId={turnaroundOriginDeliveryId?.toString()}
          onComplete={onCompleteReceiveDelivery}
          onClose={onCloseReceiveDeliveryDialog}
        />
      )}

      {showTurnaroundSuccessDialog && (
        <TurnaroundDestinationSuccessDialog
          destinationDeliveryId={turnaroundDestinationDeliveryId}
          onClose={() => {
            setShowTurnaroundSuccessDialog(false);
          }}
        />
      )}
    </>
  );
};
